/**
 * Sprint 16 — Day Zero Card
 * Sprint 17 — Readiness checklist integrated on top of the pre_live/live_pending states.
 *
 * Renders one of three states:
 *   - pre_live      → CTA "Start Live Operations" + readiness score
 *   - live_pending  → "Live operations scheduled for <date>" + readiness score
 *   - live          → subtle "LIVE" chip (no banner)
 *
 * In pre_live / live_pending states the card also renders:
 *   - Readiness score (0-100)
 *   - Days until live
 *   - Top 3 missing/warning items
 *   - "Eksikleri tamamla" button that opens the wizard at the relevant step
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Rocket, Radio, CalendarClock, CheckCircle2, AlertTriangle,
  Circle, ChevronRight, Undo2,
} from "lucide-react";
import StartLiveOpsWizard from "./StartLiveOpsWizard";
import { toast } from "sonner";

const T = {
  tr: {
    liveNow: "Canlı",
    liveScheduled: (d) => `${d} için canlı operasyon planlandı`,
    startWizard: "Canlı Operasyonu Başlat",
    preLiveHeadline: "GastroPilot'u canlı operasyonlar için hazırla",
    preLiveSub: "Açılış bakiyeleri, envanter, açık borçlar ve sabit ödemelerle temiz bir başlangıç kur.",
    scheduledSub: (n) => `${n > 0 ? n + ' gün kaldı' : 'Bugün başlıyor'}. Öncesindeki tüm veriler arşivde.`,
    liveSub: "Her fatura, ödeme ve stok hareketi canlı sistemi güncelliyor.",
    ownerOnly: "Sihirbaz yalnızca işletme sahibinde açık.",
    readiness: "Canlı Hazırlık",
    completeAll: "Eksikleri tamamla",
    ready: "Hazır",
    needsAttention: "Dikkat",
    notReady: "Hazır değil",
    daysLeft: (n) => `${n} gün kaldı`,
    launchesToday: "Bugün başlıyor",
    complete: "tamam",
    missingCount: (m, w) => (w > 0 ? `${m} eksik · ${w} uyarı` : `${m} eksik`),
    topIssues: "Öne çıkan eksikler",
    // Sprint 18 rollback
    undoTitle: "Canlı başlatmayı geri al?",
    undoBody: (h) =>
      `${h.toFixed(0)} saat içinde geri alınabilir. Tüm arşivlenmiş veri iade edilir, açılış bakiyeleri silinir.`,
    undoCta: "Geri al",
    undoYes: "Evet, geri al",
    undoNo: "Vazgeç",
    undoDone: "Canlı başlatma geri alındı.",
    undoWindow: (h) => `${h.toFixed(0)} saat kaldı`,
  },
  de: {
    liveNow: "LIVE",
    liveScheduled: (d) => `Live-Betrieb geplant für ${d}`,
    startWizard: "Live-Betrieb starten",
    preLiveHeadline: "GastroPilot für den Live-Betrieb vorbereiten",
    preLiveSub: "Sauberer Start mit Eröffnungsbilanzen, Bestand, offenen Verbindlichkeiten und Fixzahlungen.",
    scheduledSub: (n) => `${n > 0 ? 'Noch ' + n + ' Tage' : 'Startet heute'}. Alle vorherigen Daten im Archiv.`,
    liveSub: "Jede Rechnung, Zahlung und Bestandsbewegung aktualisiert das Live-System.",
    ownerOnly: "Der Assistent ist nur für den Inhaber verfügbar.",
    readiness: "Live-Bereitschaft",
    completeAll: "Lücken schließen",
    ready: "Bereit",
    needsAttention: "Beachtung",
    notReady: "Nicht bereit",
    daysLeft: (n) => `Noch ${n} Tage`,
    launchesToday: "Startet heute",
    complete: "erledigt",
    missingCount: (m, w) => (w > 0 ? `${m} fehlt · ${w} Warnung` : `${m} fehlt`),
    topIssues: "Wichtigste offene Punkte",
    // Sprint 18 rollback
    undoTitle: "Live-Start rückgängig machen?",
    undoBody: (h) =>
      `Rückgängig innerhalb von ${h.toFixed(0)} Stunden möglich. Archivierte Daten werden wiederhergestellt, Eröffnungssalden entfernt.`,
    undoCta: "Rückgängig",
    undoYes: "Ja, rückgängig",
    undoNo: "Abbrechen",
    undoDone: "Live-Start wurde rückgängig gemacht.",
    undoWindow: (h) => `${h.toFixed(0)} Stunden übrig`,
  },
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00Z");
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

function formatDe(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

const STATUS_TONE = {
  ready:            { bg: "bg-emerald-50",  border: "border-emerald-300", text: "text-emerald-800", ring: "hsl(158 64% 42%)" },
  needs_attention:  { bg: "bg-amber-50",    border: "border-amber-300",   text: "text-amber-900",    ring: "hsl(40 96% 55%)" },
  not_ready:        { bg: "bg-rose-50",     border: "border-rose-300",    text: "text-rose-900",     ring: "hsl(0 84% 60%)" },
};

const ITEM_ICON = {
  complete: CheckCircle2,
  warning:  AlertTriangle,
  missing:  Circle,
};
const ITEM_COLOR = {
  complete: "text-emerald-600",
  warning:  "text-amber-600",
  missing:  "text-rose-600",
};

export default function DayZeroCard({ onModeChanged }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const t = T[lang] || T.tr;
  const [status, setStatus] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [rollback, setRollback] = useState(null);        // {can_rollback, hours_remaining, …}
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  const load = async () => {
    try {
      const [s, r, rb] = await Promise.all([
        api.get("/day-zero/status"),
        api.get("/day-zero/readiness").catch(() => ({ data: null })),
        api.get("/day-zero/rollback-status").catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setReadiness(r.data);
      setRollback(rb.data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  if (!status) return null;
  const mode = status.operating_mode;
  const canWriteWizard = user?.role === "owner";

  // Handler: open the wizard at the step for the first non-complete item.
  const openWizardAtFirstGap = () => {
    if (!canWriteWizard) return;
    const firstGap = (readiness?.items || []).find(
      (i) => i.status !== "complete" && i.wizard_step !== null && i.wizard_step !== undefined,
    );
    setWizardStep(firstGap?.wizard_step ?? 0);
    setWizardOpen(true);
  };

  const openWizardAtStep = (stepIdx) => {
    if (!canWriteWizard) return;
    setWizardStep(stepIdx ?? 0);
    setWizardOpen(true);
  };

  const doRollback = async () => {
    setRollingBack(true);
    try {
      await api.post("/day-zero/rollback");
      toast.success(t.undoDone);
      setRollbackOpen(false);
      await load();
      onModeChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  // Sprint 18 — rollback banner is shown in live_pending/live when within grace window.
  const rollbackBanner = (rollback?.can_rollback && canWriteWizard) ? (
    <div className="flex items-center gap-2 mt-3 rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2"
         data-testid="day-zero-rollback-banner">
      <Undo2 className="w-4 h-4 text-amber-700 shrink-0" />
      <div className="flex-1 text-xs text-amber-900 min-w-0 truncate">
        {t.undoWindow(rollback.hours_remaining || 0)}
      </div>
      <Button variant="outline" size="sm"
              onClick={() => setRollbackOpen(true)}
              data-testid="day-zero-rollback-open"
              className="h-7 rounded-md text-xs">
        <Undo2 className="w-3.5 h-3.5 mr-1" /> {t.undoCta}
      </Button>
    </div>
  ) : null;

  // ==========================================================================
  //  LIVE — subtle chip, plus rollback banner (while grace window active).
  // ==========================================================================
  if (mode === "live") {
    return (
      <>
        <Card className="border-emerald-300 bg-emerald-50/50" data-testid="day-zero-card-live">
          <CardContent className="p-3 flex items-center gap-3">
            <Radio className="w-4 h-4 text-emerald-600 animate-pulse" />
            <div className="flex-1 text-xs text-emerald-900 font-medium">{t.liveSub}</div>
            <Badge className="bg-emerald-600 text-white border-0" data-testid="day-zero-badge">
              {t.liveNow}
            </Badge>
          </CardContent>
        </Card>
        {rollbackBanner}
        {renderRollbackDialog()}
      </>
    );
  }

  function renderRollbackDialog() {
    return (
      <AlertDialog open={rollbackOpen} onOpenChange={setRollbackOpen}>
        <AlertDialogContent data-testid="day-zero-rollback-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-700" /> {t.undoTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t.undoBody(rollback?.hours_remaining || 0)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="day-zero-rollback-cancel">{t.undoNo}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRollback}
              disabled={rollingBack}
              data-testid="day-zero-rollback-confirm"
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Undo2 className="w-4 h-4 mr-2" /> {t.undoYes}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // ==========================================================================
  //  PRE-LIVE + LIVE-PENDING share the readiness section.
  // ==========================================================================
  const isPending = mode === "live_pending";
  const tone = STATUS_TONE[readiness?.overall_status || "not_ready"];
  const daysLeft = daysUntil(status.live_start_date || readiness?.live_start_date || "2026-08-01");
  const missingItems = (readiness?.items || []).filter((i) => i.status !== "complete");
  const top3 = missingItems.slice(0, 3);

  return (
    <>
      {/* Header banner — differs slightly between pre_live and live_pending */}
      {isPending ? (
        <Card className="border-amber-300 bg-amber-50" data-testid="day-zero-card-live-pending">
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarClock className="w-6 h-6 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-amber-900">
                {t.liveScheduled(formatDe(status.live_start_date))}
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {t.scheduledSub(daysLeft ?? 0)}
              </div>
            </div>
            <Badge variant="outline" className="border-amber-500 text-amber-900 bg-white"
                   data-testid="day-zero-badge">
              {status.live_start_date}
            </Badge>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent"
              data-testid="day-zero-card-pre-live">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Rocket className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-base tracking-tight">
                {t.preLiveHeadline}
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {canWriteWizard ? t.preLiveSub : t.ownerOnly}
              </div>
            </div>
            <Button
              onClick={() => openWizardAtStep(0)}
              disabled={!canWriteWizard}
              data-testid="day-zero-open-wizard"
              className="shrink-0"
            >
              <Rocket className="w-4 h-4 mr-2" /> {t.startWizard}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sprint 17 — Readiness checklist section */}
      {readiness && (
        <Card className={`border ${tone.border} ${tone.bg}`} data-testid="day-zero-readiness-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {/* Radial score */}
              <ReadinessRadial
                score={readiness.readiness_score}
                ring={tone.ring}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`font-display font-semibold text-sm tracking-tight ${tone.text}`}>
                    {t.readiness}
                  </div>
                  <Badge
                    variant="outline"
                    className={`${tone.text} bg-white/70 ${tone.border} text-[10px] uppercase tracking-wider font-semibold`}
                    data-testid="readiness-status-badge"
                  >
                    {readiness.overall_status === "ready" ? t.ready :
                     readiness.overall_status === "needs_attention" ? t.needsAttention : t.notReady}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px] font-mono"
                         data-testid="readiness-days-badge">
                    {daysLeft > 0 ? t.daysLeft(daysLeft) : t.launchesToday}
                  </Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  <span data-testid="readiness-progress">
                    {readiness.counts.complete}/{readiness.counts.total} {t.complete}
                  </span>
                  {readiness.counts.missing + readiness.counts.warnings > 0 && (
                    <span className="ml-2">
                      · {t.missingCount(readiness.counts.missing, readiness.counts.warnings)}
                    </span>
                  )}
                </div>

                {/* Top-3 issues */}
                {top3.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1.5">
                      {t.topIssues}
                    </div>
                    <div className="space-y-1.5">
                      {top3.map((it) => {
                        const Icon = ITEM_ICON[it.status] || Circle;
                        const color = ITEM_COLOR[it.status] || "text-muted-foreground";
                        return (
                          <button
                            key={it.key}
                            onClick={() => openWizardAtStep(it.wizard_step)}
                            disabled={!canWriteWizard || it.wizard_step === null}
                            data-testid={`readiness-item-${it.key}`}
                            className="w-full flex items-start gap-2 text-left rounded-md px-2 py-1.5 hover:bg-white/60 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                          >
                            <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color}`} />
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-semibold truncate ${tone.text}`}>
                                {it.label?.[lang] || it.label?.tr}
                              </div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {it.recommended_action?.[lang] || it.recommended_action?.tr}
                              </div>
                            </div>
                            {canWriteWizard && it.wizard_step !== null && (
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Complete-all CTA */}
              {missingItems.length > 0 && canWriteWizard && (
                <Button
                  size="sm"
                  onClick={openWizardAtFirstGap}
                  data-testid="readiness-complete-all"
                  className="shrink-0 hidden md:inline-flex"
                >
                  {t.completeAll}
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
            {/* Mobile CTA */}
            {missingItems.length > 0 && canWriteWizard && (
              <Button
                size="sm"
                onClick={openWizardAtFirstGap}
                data-testid="readiness-complete-all-mobile"
                className="w-full mt-3 md:hidden"
              >
                {t.completeAll}
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {rollbackBanner}

      <StartLiveOpsWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialStep={wizardStep}
        onCompleted={() => { load(); onModeChanged?.(); }}
      />

      {renderRollbackDialog()}
    </>
  );
}

// ---------- Radial score ----------
function ReadinessRadial({ score, ring }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const R = 30;
  const C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;
  return (
    <div className="relative shrink-0" style={{ width: 72, height: 72 }}>
      <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
        <circle cx="36" cy="36" r={R} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="7" />
        <circle
          cx="36" cy="36" r={R} fill="none" strokeLinecap="round" strokeWidth="7"
          stroke={ring}
          strokeDasharray={`${dash} ${C - dash}`}
          style={{ transition: "stroke-dasharray 500ms cubic-bezier(0.16, 1, 0.3, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono text-lg font-bold" data-testid="readiness-score">{pct}</div>
        <div className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">/100</div>
      </div>
    </div>
  );
}
